import { Button } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";

/**
 * Choosing a photograph, framing it, and turning it into the one shape the server will accept.
 *
 * ==========================================================================================
 * THE CANVAS DOES FOUR JOBS AND ONLY ONE OF THEM IS VISIBLE
 * ==========================================================================================
 *
 * The frame exists so a member can say which part of their photograph is them. Drawing that frame
 * into a 256x256 canvas then satisfies three more requirements as a by-product:
 *
 *   FR-008  — the output is 256x256 because the canvas is; it cannot be anything else.
 *   FR-009c — a canvas encodes pixels, so no EXIF survives. No location, no capture time, no device.
 *   FR-005a — an animated source contributes a single frame.
 *
 * That is why there is no image processing on the server: it would be a native dependency added to
 * redo work already done correctly here. The server still validates what arrives, because "the
 * client did it" is not a guarantee — see `AvatarImage.create`.
 *
 * ==========================================================================================
 * `drawImage` AND NOT `createImageBitmap`
 * ==========================================================================================
 *
 * FR-009d says a photograph taken rotated must be stored the right way up. Drawing an
 * `HTMLImageElement` applies the EXIF orientation flag by default. `createImageBitmap` does NOT: its
 * `imageOrientation` option defaults to `none`, and it has to be asked explicitly to honour the
 * file's own orientation. Reaching for it is the natural modernisation of this file, and it fails on
 * exactly the phone uploads the requirement is about — every screenshot you test with is unrotated,
 * so it looks correct until somebody uses a camera.
 *
 * ==========================================================================================
 * WHY THE LIMITS ARE CHECKED HERE AS WELL AS ON THE SERVER
 * ==========================================================================================
 *
 * Not as a security measure — the server's checks are the ones that count. This is so a member who
 * picks a 12 MB photograph is told immediately, by the control they are using, rather than after an
 * upload that was never going to succeed. FR-006's 5 MB is on the FILE THEY CHOSE; what leaves here
 * is always ~10 kB whatever they started with.
 */

/** FR-005. Checked here for the message; the server re-reads the magic bytes regardless. */
const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];
const ACCEPT_ATTR = ACCEPTED.join(",");

/** FR-006, on the chosen file. */
const MAX_FILE_BYTES = 5 * 1024 * 1024;

/** FR-008. The frame is shown larger than this and scaled down on the way out. */
const OUTPUT_EDGE = 256;

/** The frame's on-screen size. Independent of `OUTPUT_EDGE`, which is what gets stored. */
const FRAME_PX = 224;

export interface AvatarPickerProps {
  /** Called with base64 WebP, ready for `PUT /api/me/avatar`. */
  onChoose: (base64Webp: string) => void;
  onCancel: () => void;
  busy?: boolean;
}

interface Loaded {
  image: HTMLImageElement;
  /** The scale at which the image exactly covers the frame — the minimum that leaves no gap. */
  coverScale: number;
}

export function AvatarPicker({ onChoose, onCancel, busy = false }: AvatarPickerProps): ReactNode {
  const t = useT();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragging = useRef<{ x: number; y: number } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Revoke the object URL when the chosen image changes or the picker unmounts. Without this every
  // re-pick leaks the previous file for the lifetime of the tab.
  useEffect(() => {
    const url = loaded?.image.src;
    return () => {
      if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
    };
  }, [loaded]);

  function choose(file: File | undefined): void {
    if (!file) return;
    setError(null);

    if (!ACCEPTED.includes(file.type)) {
      setError(t("identity.avatar.wrongFormat"));
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError(t("identity.avatar.tooLarge"));
      return;
    }

    const image = new Image();
    const url = URL.createObjectURL(file);
    image.onload = () => {
      // `cover`, so the frame is never partly empty however the photograph is proportioned.
      const coverScale = FRAME_PX / Math.min(image.naturalWidth, image.naturalHeight);
      setLoaded({ image, coverScale });
      setZoom(1);
      setOffset({ x: 0, y: 0 });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      setError(t("identity.avatar.notAnImage"));
    };
    image.src = url;
  }

  /** Draw the framed region at the stored size and hand back base64. */
  function confirm(): void {
    const canvas = canvasRef.current;
    if (!canvas || !loaded) return;

    const context = canvas.getContext("2d");
    if (!context) {
      setError(t("identity.avatar.prepareFailed"));
      return;
    }

    const scale = loaded.coverScale * zoom;
    const drawnWidth = loaded.image.naturalWidth * scale;
    const drawnHeight = loaded.image.naturalHeight * scale;

    // The frame is a window onto the scaled image; the canvas is that window at OUTPUT_EDGE.
    const ratio = OUTPUT_EDGE / FRAME_PX;
    context.clearRect(0, 0, OUTPUT_EDGE, OUTPUT_EDGE);
    context.drawImage(
      loaded.image,
      (offset.x + (FRAME_PX - drawnWidth) / 2) * ratio,
      (offset.y + (FRAME_PX - drawnHeight) / 2) * ratio,
      drawnWidth * ratio,
      drawnHeight * ratio,
    );

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setError(t("identity.avatar.prepareFailed"));
          return;
        }
        void blob.arrayBuffer().then((buffer) => {
          const bytes = new Uint8Array(buffer);
          let binary = "";
          const CHUNK = 0x8000;
          for (let i = 0; i < bytes.length; i += CHUNK)
            binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
          onChoose(btoa(binary));
        });
      },
      "image/webp",
      0.85,
    );
  }

  const scale = loaded ? loaded.coverScale * zoom : 1;

  return (
    <div className="mt-4">
      {loaded === null ? (
        <>
          <label
            className="inline-flex cursor-pointer items-center rounded-control border border-input bg-input/30 px-4 py-2 text-sm font-medium text-ink hover:bg-accent"
            htmlFor="avatar-file"
          >
            {t("identity.avatar.choose")}
          </label>
          <input
            id="avatar-file"
            type="file"
            accept={ACCEPT_ATTR}
            className="sr-only"
            data-testid="avatar-file"
            onChange={(event) => {
              choose(event.target.files?.[0]);
              // Reset, so choosing the SAME file twice after a cancel still fires `change`.
              event.target.value = "";
            }}
          />
        </>
      ) : (
        <>
          <p className="text-sm text-muted">{t("identity.avatar.reposition")}</p>
          {/*
            The frame. `overflow-hidden` with a round mask is what makes the preview honest — the
            member sees the circle their picture will actually appear in, at every other size in the
            application.
          */}
          <div
            className="mt-2 touch-none overflow-hidden rounded-full border border-line select-none"
            style={{ width: FRAME_PX, height: FRAME_PX }}
            onPointerDown={(event: ReactPointerEvent<HTMLDivElement>) => {
              dragging.current = { x: event.clientX - offset.x, y: event.clientY - offset.y };
              event.currentTarget.setPointerCapture(event.pointerId);
            }}
            onPointerMove={(event: ReactPointerEvent<HTMLDivElement>) => {
              const start = dragging.current;
              if (!start) return;
              setOffset({ x: event.clientX - start.x, y: event.clientY - start.y });
            }}
            onPointerUp={(event: ReactPointerEvent<HTMLDivElement>) => {
              dragging.current = null;
              event.currentTarget.releasePointerCapture(event.pointerId);
            }}
          >
            <img
              alt=""
              src={loaded.image.src}
              draggable={false}
              style={{
                width: loaded.image.naturalWidth * scale,
                height: loaded.image.naturalHeight * scale,
                transform: `translate(${String(offset.x)}px, ${String(offset.y)}px)`,
                marginLeft: (FRAME_PX - loaded.image.naturalWidth * scale) / 2,
                marginTop: (FRAME_PX - loaded.image.naturalHeight * scale) / 2,
                maxWidth: "none",
              }}
            />
          </div>

          <label className="mt-3 block text-sm text-muted" htmlFor="avatar-zoom">
            {t("identity.avatar.zoom")}
          </label>
          <input
            id="avatar-zoom"
            type="range"
            min="1"
            max="3"
            step="0.01"
            value={zoom}
            className="w-56"
            onChange={(event) => setZoom(Number(event.target.value))}
          />

          <div className="mt-4 flex gap-2">
            <Button variant="primary" onClick={confirm} disabled={busy} data-testid="avatar-save">
              {busy ? t("common.saving") : t("identity.avatar.save")}
            </Button>
            <Button
              onClick={() => {
                setLoaded(null);
                setError(null);
                onCancel();
              }}
              disabled={busy}
            >
              {t("common.cancel")}
            </Button>
          </div>
        </>
      )}

      {/* Off-screen and never shown: it exists to be drawn into, not looked at. */}
      <canvas ref={canvasRef} width={OUTPUT_EDGE} height={OUTPUT_EDGE} className="hidden" />

      {error === null ? null : (
        <p className="mt-3 text-sm text-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
